import React, { useContext } from 'react';
import PropTypes from 'prop-types';

import FileUploadAccordian from '../../file-upload-accordian';
import {
  LeaseAbstractionField,
  LeaseAbstractionFieldType,
} from '../../leaseAbstractionField';
import AddItem from '../../../../nucleus/add-item/add-item';

import '../../lease-abstraction.scss';
import CustomLeaseEscalationField from './components/customLeaseEscalationField';
import { getRandomId } from '../../../../utils/validation-utils';
import LeaseAbstractionContext from '../../contexts/leaseAbstractionContext';
import {
  convertFreeRentMonthsToYearsAndMonths,
  getFreeRentRequiredError,
  getUpdatedFreeRentTotalMonths,
  isBlankFreeRentValue,
} from '../../../../utils/free-rent-utils';

const leaseEscalationOptions = [
  { display_name: 'Fixed', value: 'Fixed' },
  { display_name: 'Custom', value: 'Custom' },
];

const parseBooleanValue = value => value === true || String(value).toLowerCase() === 'true';

const normalizeCustomEscalationPayload = schedule => schedule.map((item) => {
  const rent = item.rent || item;
  return {
    months: parseInt(item.months, 10) || 0,
    rent_dollar: Number(rent.rent_dollar) || 0,
    rent_percent: Number(rent.rent_percent) || 0,
    is_percent: parseBooleanValue(rent.is_percent),
  };
});

const RentAndEscalations = ({ storageKey }) => {
  const {
    modalFieldState,
    modalFieldChangeHandler,
    individualModalFieldChangeHandler,
    fieldErrorMap,
    validateLeaseAbstractionFields,
    showPageValidation,
    saveCompField,
    clearFieldError,
    isEarlyInvoice,
  } = useContext(LeaseAbstractionContext);
  const showRequiredValidation = showPageValidation && !isEarlyInvoice;

  // Shared by blur (below) and the $/% type-change handler -- only the currently-active
  // field is sent, since the inactive one is always forced to 0 in local state (see
  // calculatePercentValueFromTotal's baseTotal=0 handling).
  const saveFixedEscalationValue = (value) => {
    const isPercent = parseBooleanValue(value.rent_escalation_is_percent);
    saveCompField('fixedLeaseEscalation', {
      rent_escalation_is_percent: value.rent_escalation_is_percent,
      ...(isPercent
        ? { rent_escalation_percent: value.rent_escalation_percent }
        : { rent_escalation_dollar: value.rent_escalation_dollar }),
    });
  };

  return (
    <FileUploadAccordian accordianTitle="Rent & Escalation" storageKey={storageKey}>
      <LeaseAbstractionField
        key="baseRent"
        fieldType={LeaseAbstractionFieldType.TEXT}
        isRequired={!isEarlyInvoice}
        fieldData={{
          data: modalFieldState.baseRent,
          label: 'Base Rent($/SqFt per year)',
          errorMessage: (!modalFieldState.baseRent.value && showRequiredValidation) ? 'Base Rent ($/SF per year) is required' : fieldErrorMap.baseRent,
          key: 'baseRent',
        }}
        fieldChangeHandler={(event) => {
          const newValue = event.target.value;
          const regex = /^\d{0,11}(\.\d{0,2})?$/;
          if (regex.test(newValue) || newValue === '') {
            modalFieldChangeHandler('baseRent', { value: newValue });
          }
        }}
        onBlur={() => saveCompField('baseRent', modalFieldState.baseRent.value)}
        acceptFieldChangeHandler={(extractedVal) => {
          modalFieldChangeHandler('baseRent', { value: String(extractedVal) }, 'single');
          clearFieldError('baseRent');
          saveCompField('baseRent', extractedVal);
        }}
        individualFieldChangeHandler={(event) => {
          individualModalFieldChangeHandler({
            child_value: event.target.value,
            parent_key: 'comp_info',
            child_key: modalFieldState.baseRent.trackingID,
            stateVariable: 'baseRent',
          });
        }}
        validateLeaseAbstractionFields={validateLeaseAbstractionFields}
      />
      <div className="file-modal-flex-group">
        <LeaseAbstractionField
          asGroup
          isRequired={!isEarlyInvoice}
          inputText="Years"
          key="freeRentYears"
          fieldType={LeaseAbstractionFieldType.TEXT_WITH_LABEL}
          fieldData={{
            data: {
              ...modalFieldState.freeRent,
              value: convertFreeRentMonthsToYearsAndMonths(modalFieldState.freeRent.value)
                .years,
              // Compare against total months (same unit as extractedValue) not years
              chipDisplayValue: modalFieldState.freeRent.value,
            },
            label: 'Free Rent',
            errorMessage: getFreeRentRequiredError(modalFieldState.freeRent.value, showRequiredValidation, fieldErrorMap.freeRent),
            key: 'freeRent',
          }}
          fieldChangeHandler={(event) => {
            const newValue = event.target.value;
            const regex = /^\d{0,4}(\.\d*)?$/;
            if (regex.test(newValue) || newValue === '') {
              modalFieldChangeHandler('freeRent', {
                value: getUpdatedFreeRentTotalMonths({
                  currentTotalMonths: modalFieldState.freeRent.value,
                  part: 'years',
                  value: newValue,
                }),
              });
            }
          }}
          onBlur={() => saveCompField('freeRent', modalFieldState.freeRent.value)}
          acceptFieldChangeHandler={(extractedVal) => {
            // extractedVal from AI is total months (number or string)
            const totalMonths = isBlankFreeRentValue(extractedVal) ? null : parseInt(extractedVal, 10) || 0;
            modalFieldChangeHandler('freeRent', { value: totalMonths }, 'single');
            clearFieldError('freeRent');
            saveCompField('freeRent', totalMonths);
          }}
          individualFieldChangeHandler={(event) => {
            const newValue = event.target.value;
            const regex = /^\d{0,4}(\.\d*)?$/;
            if (regex.test(newValue) || newValue === '') {
              const newFreeRentValue = getUpdatedFreeRentTotalMonths({
                currentTotalMonths: modalFieldState.freeRent.value,
                part: 'years',
                value: newValue,
              });
              modalFieldChangeHandler('freeRent', { value: newFreeRentValue });
              // Fire attachmentprops API for old flow. No-op in new enhanced flow.
              individualModalFieldChangeHandler({
                child_value: newFreeRentValue,
                parent_key: 'comp_info',
                child_key: modalFieldState.freeRent.trackingID,
                stateVariable: 'freeRent',
              });
            }
          }}
          validateLeaseAbstractionFields={validateLeaseAbstractionFields}
        />
        <LeaseAbstractionField
          asGroup
          showToolTip={false}
          inputText="Months"
          key="freeRentMonths"
          fieldType={LeaseAbstractionFieldType.TEXT}
          fieldData={{
            data: {
              ...modalFieldState.freeRent,
              value: convertFreeRentMonthsToYearsAndMonths(modalFieldState.freeRent.value)
                .months,
            },
            errorMessage: getFreeRentRequiredError(modalFieldState.freeRent.value, showRequiredValidation, fieldErrorMap.freeRent),
            key: 'freeRent',
          }}
          fieldChangeHandler={(event) => {
            const newValue = event.target.value;
            const regex = /^\d{0,4}(\.\d*)?$/;
            if (regex.test(newValue) || newValue === '') {
              const roundedValue = newValue.includes('.') && newValue.split('.')[1].length > 4
                ? String(parseFloat(parseFloat(newValue).toFixed(4)))
                : newValue;
              modalFieldChangeHandler('freeRent', {
                value: getUpdatedFreeRentTotalMonths({
                  currentTotalMonths: modalFieldState.freeRent.value,
                  part: 'months',
                  value: roundedValue,
                }),
              });
            }
          }}
          onBlur={() => saveCompField('freeRent', modalFieldState.freeRent.value)}
          individualFieldChangeHandler={(event) => {
            const newValue = event.target.value;
            const regex = /^\d{0,4}(\.\d*)?$/;
            if (regex.test(newValue) || newValue === '') {
              const roundedValue = newValue.includes('.') && newValue.split('.')[1].length > 4
                ? String(parseFloat(parseFloat(newValue).toFixed(4)))
                : newValue;
              const newFreeRentValue = getUpdatedFreeRentTotalMonths({
                currentTotalMonths: modalFieldState.freeRent.value,
                part: 'months',
                value: roundedValue,
              });
              modalFieldChangeHandler('freeRent', { value: newFreeRentValue });
              // Fire attachmentprops API for old flow. No-op in new enhanced flow.
              individualModalFieldChangeHandler({
                child_value: newFreeRentValue,
                parent_key: 'comp_info',
                child_key: modalFieldState.freeRent.trackingID,
                stateVariable: 'freeRent',
              });
            }
          }}
          validateLeaseAbstractionFields={validateLeaseAbstractionFields}
        />
      </div>
      <LeaseAbstractionField
        key="leaseEscalation"
        fieldType={LeaseAbstractionFieldType.SELECT}
        isRequired={!isEarlyInvoice}
        fieldData={{
          data: (() => {
            // When the escalation type is Custom, also treat the chip as dirty
            // if the schedule rows differ from what was extracted.
            if (modalFieldState.leaseEscalation.value === 'Custom') {
              const extracted = modalFieldState.customLeaseEscalation.extractedValue;
              const current = modalFieldState.customLeaseEscalation.value;
              const isScheduleDirty = Array.isArray(extracted) && Array.isArray(current) && (
                extracted.length !== current.length ||
                extracted.some((ei, i) => {
                  const ci = current[i];
                  if (!ci) return true;
                  return Number(ei.months) !== Number(ci.months) ||
                    Number(ei.rent?.rent_dollar) !== Number(ci.rent?.rent_dollar) ||
                    Number(ei.rent?.rent_percent) !== Number(ci.rent?.rent_percent);
                })
              );
              if (isScheduleDirty) {
                // chipDisplayValue differs from extractedValue → chip turns purple
                return { ...modalFieldState.leaseEscalation, chipDisplayValue: 'Custom (modified)' };
              }
            }
            return modalFieldState.leaseEscalation;
          })(),
          label: 'Lease Escalation',
          errorMessage: (!modalFieldState.leaseEscalation.value && showRequiredValidation) ? 'Lease Escalation is required' : fieldErrorMap.leaseEscalation,
          options: leaseEscalationOptions,
          key: 'leaseEscalation',
        }}
        fieldChangeHandler={(event) => {
          modalFieldChangeHandler('leaseEscalation', {
            value: event.value,
          });
        }}
        acceptFieldChangeHandler={(extractedVal) => {
          modalFieldChangeHandler('leaseEscalation', { value: extractedVal }, 'single');
          clearFieldError('leaseEscalation');
          saveCompField('leaseEscalation', extractedVal);
          // Also restore the extracted schedule when accepting
          const extracted = modalFieldState.customLeaseEscalation.extractedValue;
          if (extractedVal === 'Custom' && Array.isArray(extracted) && extracted.length > 0) {
            modalFieldChangeHandler('customLeaseEscalation', { value: extracted });
            saveCompField('customLeaseEscalation', normalizeCustomEscalationPayload(extracted));
          }
        }}
        individualFieldChangeHandler={(event) => {
          individualModalFieldChangeHandler({
            child_value: event.value,
            parent_key: 'comp_info',
            child_key: modalFieldState.leaseEscalation.trackingID,
            stateVariable: 'leaseEscalation',
          });
        }}
        validateLeaseAbstractionFields={validateLeaseAbstractionFields}
      />
      {modalFieldState.leaseEscalation.value === 'Fixed' && (
        <LeaseAbstractionField
          asGroup
          percentDollarHideConversion
          customPercentDollarField={{
            select: 'rent_escalation_is_percent',
            value: 'rent_escalation_dollar',
            percentage: 'rent_escalation_percent',
          }}
          isRequired={!isEarlyInvoice}
          key="escalationPerYear"
          fieldType={LeaseAbstractionFieldType.PERCENT_DOLLAR}
          fieldData={{
            data: modalFieldState.fixedLeaseEscalation,
            label: 'Escalation (per year)',
            errorMessage: (
              showRequiredValidation &&
              modalFieldState.fixedLeaseEscalation.value?.rent_escalation_dollar == null &&
              modalFieldState.fixedLeaseEscalation.value?.rent_escalation_percent == null
            )
              ? 'Escalation (per year) is required'
              : fieldErrorMap.fixedLeaseEscalation,
            key: 'fixedLeaseEscalation',
          }}
          fieldChangeHandler={(event) => {
            // Local-only: PercentDollarInput fires this on every keystroke and on
            // $/% type change. Saving here (as this used to) fired an API call per
            // character typed. The actual save happens once, on blur, below.
            modalFieldChangeHandler('fixedLeaseEscalation', {
              value: event.value,
            });
          }}
          individualFieldChangeHandler={() => {
            saveFixedEscalationValue(modalFieldState.fixedLeaseEscalation.value || {});
          }}
          percentFieldCustomOnTypeChange={(e) => {
            // Switching $/% alone (no digit edit after) never blurs the number input, so
            // without this the type flag would update in local state only and never save.
            const updatedValue = {
              ...modalFieldState.fixedLeaseEscalation.value,
              [e.target.name]: e.target.value,
            };
            modalFieldChangeHandler('fixedLeaseEscalation', { value: updatedValue });
            saveFixedEscalationValue(updatedValue);
          }}
        />
      )}
      {modalFieldState.leaseEscalation.value === 'Custom' && (
        <>
          <div className="file-modal-sub-heading-container">
            <p className="file-modal-sub-heading">Lease Escalation Breakdown</p>
          </div>
          {modalFieldState.customLeaseEscalation.value.map((customLeaseEscalation, index) => (
            <CustomLeaseEscalationField
              modalFieldState={modalFieldState}
              modalFieldChangeHandler={modalFieldChangeHandler}
              individualModalFieldChangeHandler={
                  individualModalFieldChangeHandler
                }
              fieldErrorMap={fieldErrorMap}
              showPageValidation={showRequiredValidation}
              isRequired={!isEarlyInvoice}
              currentCustomLeaseEscalation={customLeaseEscalation}
              escalationIndex={index}
              key={customLeaseEscalation.id}
            />
            ))}
          <div className="file-modal-add-button">
            <AddItem
              label="Add Rent"
              onClick={() => {
                const newCustomLeaseEscalationValue = [
                  ...modalFieldState.customLeaseEscalation.value,
                ];
                newCustomLeaseEscalationValue.push({
                  id: getRandomId(),
                  rent: {
                    is_percent: false,
                    rent_dollar: 0,
                    rent_percent: 0,
                  },
                  months: '',
                });
                modalFieldChangeHandler('customLeaseEscalation', {
                  value: newCustomLeaseEscalationValue,
                });
                const child_value = normalizeCustomEscalationPayload(newCustomLeaseEscalationValue);
                individualModalFieldChangeHandler({
                  child_value,
                  parent_key: 'comp_info',
                  child_key: modalFieldState.customLeaseEscalation.trackingID,
                  stateVariable: 'customLeaseEscalation',
                });
              }}
            />
          </div>
        </>
      )}
    </FileUploadAccordian>
  );
};

RentAndEscalations.propTypes = {
  storageKey: PropTypes.string,
};

RentAndEscalations.defaultProps = {
  storageKey: '',
};

export default RentAndEscalations;
